import { ShapeHandler } from "./shape-handler.interface";
import { Annotation } from "../models/annotation.model";

/**
 * Handler for link-specific behavior
 * Implements the ShapeHandler interface for link annotations
 */
export class LinkHandler implements ShapeHandler {
  /**
   * Gets the left position (percentage) of a link annotation
   */
  getLeft(annotation: Annotation): number {
    return (annotation as any).x;
  }

  /**
   * Gets the top position (percentage) of a link annotation
   */
  getTop(annotation: Annotation): number {
    return (annotation as any).y;
  }

  /**
   * Gets the width (percentage) of a link annotation
   * For links, we use a reasonable default width for the clickable area
   */
  getWidth(annotation: Annotation): number {
    return (annotation as any).width || 8;
  }

  /**
   * Gets the height (percentage) of a link annotation
   * For links, we use a reasonable default height for the clickable area
   */
  getHeight(annotation: Annotation): number {
    return (annotation as any).height || 4;
  }

  /**
   * Gets the padding-bottom value for links (always null)
   */
  getPaddingBottom(annotation: Annotation): number | null {
    return null;
  }

  /**
   * Updates a link annotation during drag operations based on the drag mode
   */
  updateOnDrag(
    dragMode: string,
    annotation: any,
    dragStart: {
      x?: number;
      y?: number;
      width?: number;
      height?: number;
    },
    deltaXPercent: number,
    deltaYPercent: number,
    minSizePercent: number
  ): any {
    const result = { ...annotation };

    if (dragMode === "whole") {
      // Move the entire link annotation
      result.x = Math.max(0, Math.min(100, (dragStart.x || 0) + deltaXPercent));
      result.y = Math.max(0, Math.min(100, (dragStart.y || 0) + deltaYPercent));
    } else if (dragMode === "resize") {
      // Resize link by dragging with increased sensitivity to match text behavior
      const newHeight = Math.max(minSizePercent, (dragStart.height || 4) - deltaYPercent * 2.5);
      const newWidth = Math.max(minSizePercent, (dragStart.width || 8) + deltaXPercent * 2.5);

      result.y = dragStart.y || 0;
      result.x = dragStart.x || 0;
      result.height = newHeight;
      result.width = newWidth;

      // Reasonable limits for link annotations - increased to match text capabilities
      if (newHeight < 3) {
        result.height = 3;
      } else if (newHeight > 60) {
        result.height = 60; // Increased from 20 to 60
      }

      if (newWidth < 5) {
        result.width = 5;
      } else if (newWidth > 80) {
        result.width = 80; // Increased from 40 to 80
      }
    }

    // Ensure link stays within bounds
    result.x = Math.max(0, Math.min(100, result.x));
    result.y = Math.max(0, Math.min(100, result.y));
    result.width = Math.max(minSizePercent, Math.min(100, result.width));
    result.height = Math.max(minSizePercent, Math.min(100, result.height));

    return result;
  }

  /**
   * Converts a link annotation to points array for storage
   */
  toPoints(annotation: any): { x: number; y: number }[] {
    return [
      { x: annotation.x, y: annotation.y },
      { x: annotation.x + (annotation.width || 8), y: annotation.y + (annotation.height || 4) }
    ];
  }
}
